import React, { useState, useEffect } from 'react';
import { Box, TextField, Select, MenuItem, FormHelperText } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, parse } from 'date-fns';

const DateRangeFilter = ({ value, onChange, error, columnName }) => {
  const [operator, setOperator] = useState('between');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [singleDate, setSingleDate] = useState(null);

  useEffect(() => {
    // Parse incoming SQL filter value
    if (value) {
      if (value.toLowerCase().includes('between')) {
        const matches = value.match(/BETWEEN\s+DATE\('(\d{4}-\d{2}-\d{2})'\)\s+AND\s+DATE\('(\d{4}-\d{2}-\d{2})'\)/i);
        if (matches) {
          setOperator('between');
          setStartDate(parse(matches[1], 'yyyy-MM-dd', new Date()));
          setEndDate(parse(matches[2], 'yyyy-MM-dd', new Date()));
        }
      } else if (value.match(/[<>]=?\s*DATE\('\d{4}-\d{2}-\d{2}'\)/)) {
        const matches = value.match(/([<>]=?)\s*DATE\('(\d{4}-\d{2}-\d{2})'\)/);
        if (matches) {
          setOperator(matches[1]);
          setSingleDate(parse(matches[2], 'yyyy-MM-dd', new Date()));
        }
      } else if (value.match(/=\s*DATE\('\d{4}-\d{2}-\d{2}'\)/)) {
        const matches = value.match(/=\s*DATE\('(\d{4}-\d{2}-\d{2})'\)/);
        if (matches) {
          setOperator('=');
          setSingleDate(parse(matches[1], 'yyyy-MM-dd', new Date()));
        }
      }
    }
  }, [value]);

  const handleChange = (field, newValue) => {
    // Keep track of current values to generate SQL
    let currentStartDate = startDate;
    let currentEndDate = endDate;
    let currentSingleDate = singleDate;
    let currentOperator = operator;

    if (field === 'operator') {
      currentOperator = newValue;
      // Reset values when switching operators
      if (newValue === 'between') {
        currentSingleDate = null;
      } else {
        currentStartDate = null;
        currentEndDate = null;
      }
      setOperator(newValue);
    } else if (field === 'start') {
      currentStartDate = newValue;
      setStartDate(newValue);
    } else if (field === 'end') {
      currentEndDate = newValue;
      setEndDate(newValue);
    } else if (field === 'single') {
      currentSingleDate = newValue;
      setSingleDate(newValue);
    }

    // Generate SQL using the current values instead of state
    let sqlFilter = '';
    if (currentOperator === 'between') {
      if (currentStartDate && currentEndDate) {
        sqlFilter = `${columnName} BETWEEN DATE('${format(currentStartDate, 'yyyy-MM-dd')}') AND DATE('${format(currentEndDate, 'yyyy-MM-dd')}')`;
      }
    } else if (currentSingleDate) {
      sqlFilter = `${columnName} ${currentOperator} DATE('${format(currentSingleDate, 'yyyy-MM-dd')}')`;
    }

    onChange(sqlFilter);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <Select
            value={operator}
            onChange={(e) => handleChange('operator', e.target.value)}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="between">Between</MenuItem>
            <MenuItem value="=">=</MenuItem>
            <MenuItem value=">">{'>'}</MenuItem>
            <MenuItem value="<">{'<'}</MenuItem>
            <MenuItem value=">=">{'>='}</MenuItem>
            <MenuItem value="<=">{'<='}</MenuItem>
          </Select>
          
          {operator === 'between' ? (
            <>
              <DatePicker
                value={startDate}
                onChange={(newValue) => handleChange('start', newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    error={error && !startDate}
                  />
                )}
              />
              <DatePicker
                value={endDate}
                onChange={(newValue) => handleChange('end', newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    error={error && !endDate}
                  />
                )}
              />
            </>
          ) : (
            <DatePicker
              value={singleDate}
              onChange={(newValue) => handleChange('single', newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  error={error && !singleDate}
                />
              )}
            />
          )}
        </Box>
        {error && <FormHelperText error>This field is required</FormHelperText>}
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangeFilter; 